const createAirportPinSVGAdjusted = (
  delayPercentage: number = 0,
  strokeSize: number = 2
): string => {
  const svgWidth = 32;
  const svgHeight = 32;
  const centerX = 16;
  const centerY = 16;

  // Use a slightly larger radius for the white background circle
  const whiteBackgroundRadius = 15; // Slightly less than outerCircleRadius for a subtle border look

  const innerCircleRadius = 12;
  const redStripeStrokeWidth = strokeSize;
  const redStripeRadius = (innerCircleRadius + whiteBackgroundRadius) / 2; // Adjust red stripe based on white background
  const circumference = 2 * Math.PI * redStripeRadius;
  const dashOffset = circumference * (1 - delayPercentage / 100);

  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="${svgWidth * 2}" height="${svgHeight * 2}" viewBox="0 0 ${svgWidth} ${svgHeight}" fill="none">
      <defs>
        <filter id="pinShadow" x="-50%" y="-50%" width="200%" height="200%">
          <feOffset result="offOut" in="SourceAlpha" dx="0" dy="2" />
          <feGaussianBlur result="blurOut" in="offOut" stdDeviation="2" />
          <feColorMatrix result="matrixOut" in="blurOut" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
          <feBlend in="SourceGraphic" in2="matrixOut" mode="normal" />
        </filter>
      </defs>
      <rect x="0" y="0" width="${svgWidth}" height="${svgHeight}" fill="rgba(0,0,0,0.01)" />
      <circle cx="${centerX}" cy="${centerY}" r="${whiteBackgroundRadius}" fill="white"/>
      <circle cx="${centerX}" cy="${centerY}" r="${innerCircleRadius}" fill="#F19164"/>
      <circle
          cx="${centerX}"
          cy="${centerY}"
          r="${redStripeRadius}"
          fill="none"
          stroke="#F71E44"
          stroke-width="${redStripeStrokeWidth}"
          stroke-dasharray="${circumference} ${circumference}"
          stroke-dashoffset="${dashOffset}"
          stroke-linecap="round"  transform="rotate(-90 ${centerX} ${centerY})"  opacity="0.9"
      />
      <g transform="translate(8, 8)">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="white">
          <path d="M8 1.33203C8.33114 1.33203 8.62125 1.49394 8.80273 1.74121C8.09731 2.51201 7.66699 3.5388 7.66699 4.66602C7.66717 7.0591 9.60687 8.99902 12 8.99902C12.5604 8.99902 13.0953 8.89093 13.5869 8.69727L14.667 9.33203V10.666L9 8.99902V12.666L10.667 13.666V14.666L8 13.999L5.33301 14.666V13.666L7 12.666V8.99902L1.33301 10.666V9.33203L7 5.99902V2.33203C7.00017 1.77885 7.44678 1.33203 8 1.33203ZM12 1.33203C13.8409 1.33203 15.333 2.82507 15.333 4.66602C15.3328 6.44719 13.9359 7.90176 12.1777 7.99414C12.119 7.99687 12.0598 7.99902 12 7.99902C11.9145 7.99902 11.8302 7.99418 11.7471 7.98828C10.0807 7.86321 8.75576 6.51423 8.6709 4.83691L8.66699 4.66602C8.66699 4.604 8.66889 4.54228 8.67188 4.48145V4.47949C8.7148 3.69989 9.02546 2.99162 9.51367 2.44531C9.53008 2.42702 9.54659 2.40873 9.56348 2.39062C10.1719 1.73935 11.0385 1.33203 12 1.33203ZM11.667 4.79883L13.0996 6.26562L13.5996 5.76562L12.333 4.53223V2.66602H11.667V4.79883Z" />
        </svg>
      </g>
    </svg>`;
};

/**
 * Generates a colored SVG image for map display based on transport type and alert status
 *
 * @param type - The transport type: 'air', 'air-delay', or any other value for truck
 * @param color - The hex color code for the marker background
 * @param hasAlert - Whether the shipment has any alerts
 * @param isCritical - Whether the alert is critical (only relevant if hasAlert is true)
 * @param delayPercentage - The delay percentage for air-delay markers (0-100)
 * @returns A data URI string containing the SVG image
 */
export const getColoredMarker = (
  type: string,
  color: string,
  hasAlert: boolean,
  isCritical: boolean,
  delayPercentage: number = 0,
  strokeSize: number = 0
): string => {
  const airSVG = `
      <svg width="64" height="64" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="32" height="32" rx="16" fill="${color}" stroke="white" stroke-width="1.5"/>
      <path d="M17.9998 14.6666H21.3337C22.0698 14.6668 22.6666 15.2635 22.6667 15.9996C22.6667 16.7359 22.07 17.3334 21.3337 17.3336H17.9998L13.9998 21.3336H12.6667L14.8894 17.3336H11.3337C10.5974 17.3336 9.99976 16.736 9.99976 15.9996C9.99976 15.9921 10.0006 15.9846 10.0007 15.9772L9.43335 12.6666H9.69312C10.0609 12.6666 10.3995 12.8683 10.574 13.192L11.3679 14.6666H14.8894L12.6667 10.6666H13.9998L17.9998 14.6666Z" fill="white"/>
      </svg>
    `;

  const airRiskSVG = `
      <svg width="78" height="76" viewBox="0 0 39 38" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g filter="url(#filter0_d_17010_25183)">
      <rect y="6" width="32" height="32" rx="16" fill="${color}" stroke="white" stroke-width="1.5"/>
      <path d="M17.9995 20.6666H21.3335C22.0696 20.6668 22.6663 21.2635 22.6665 21.9996C22.6665 22.7359 22.0697 23.3334 21.3335 23.3336H17.9995L13.9995 27.3336H12.6665L14.8892 23.3336H11.3335C10.5974 23.3336 9.99976 22.736 9.99976 21.9996C9.99976 21.9921 10.0006 21.9846 10.0007 21.9772L9.43335 18.6666H9.69312C10.0609 18.6666 10.3995 18.8683 10.574 19.192L11.3679 20.6666H14.8894L12.6667 16.6666H13.9998L17.9998 20.6666Z" fill="white"/>
      </g>
      <path d="M29.5405 2.35315L36.8745 15.0201L37.4175 15.9586H20.5825L21.1255 15.0201L28.4585 2.35315L28.9995 1.41956L29.5405 2.35315ZM28.9585 12.7086H29.0415V12.6246H28.9585V12.7086ZM28.9585 10.0416H29.0415V8.62463H28.9585V10.0416Z" fill="#E6A000" stroke="white" stroke-width="1.25"/>
      <defs>
      <filter id="filter0_d_17010_25183" x="-16" y="-6" width="64" height="64" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
      <feFlood flood-opacity="0" result="BackgroundImageFix"/>
      <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
      <feOffset dy="4"/>
      <feGaussianBlur stdDeviation="8"/>
      <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0"/>
      <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_17010_25183"/>
      <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_17010_25183" result="shape"/>
      </filter>
      </defs>
      </svg>
    `;

  const airCriticalSVG = `
      <svg width="74" height="74" viewBox="0 0 37 37" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g filter="url(#filter0_d_17001_25054)">
      <rect y="5" width="32" height="32" rx="16" fill="${color}" stroke="white" stroke-width="1.5"/>
      <path d="M17.9995 19.6666H21.3335C22.0696 19.6668 22.6663 20.2635 22.6665 20.9996C22.6665 21.7359 22.0697 22.3334 21.3335 22.3336H17.9995L13.9995 26.3336H12.6665L14.8892 22.3336H11.3335C10.5971 22.3336 9.99951 21.736 9.99951 20.9996C9.99951 20.9921 10.0004 20.9846 10.0005 20.9772L9.43311 17.6666H9.69287C10.0606 17.6666 10.3993 17.8683 10.5737 18.192L11.3677 19.6666H14.8892L12.6665 15.6666H13.9995L17.9995 19.6666Z" fill="white"/>
      <rect x="21" width="16" height="16" rx="8" fill="white"/>
      <path d="M29.0002 1.33337C25.3202 1.33337 22.3335 4.32004 22.3335 8.00004C22.3335 11.68 25.3202 14.6667 29.0002 14.6667C32.6802 14.6667 35.6668 11.68 35.6668 8.00004C35.6668 4.32004 32.6802 1.33337 29.0002 1.33337ZM29.6668 11.3334H28.3335V10H29.6668V11.3334ZM29.6668 8.66671H28.3335V4.66671H29.6668V8.66671Z" fill="#DE1135"/>
      </g>
      <defs>
      <filter id="filter0_d_17001_25054" x="-16" y="-12" width="69" height="69" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
      <feFlood flood-opacity="0" result="BackgroundImageFix"/>
      <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
      <feOffset dy="4"/>
      <feGaussianBlur stdDeviation="8"/>
      <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0"/>
      <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_17001_25054"/>
      <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_17001_25054" result="shape"/>
      </filter>
      </defs>
      </svg>
    `;

  const truckSVG = `
      <svg width="64" height="64" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="32" height="32" rx="16" fill="${color}" stroke="white" stroke-width="1.5"/>
      <path d="M19.333 13.333H21.333L23.333 16V19.333H21.7119L21.7031 19.5049C21.6171 20.3532 20.9003 21.0156 20.0293 21.0156C19.1584 21.0154 18.4424 20.3531 18.3564 19.5049L18.3477 19.333H13.6816L13.6729 19.5049C13.5868 20.3531 12.871 21.0156 12 21.0156C11.129 21.0156 10.4132 20.3531 10.3271 19.5049L10.3184 19.333H8.66699V12C8.66699 11.6333 8.79748 11.3197 9.05859 11.0586C9.3197 10.7975 9.63333 10.667 10 10.667H19.333V13.333ZM19.333 14.667V16.667H22.167L20.667 14.667H19.333Z" fill="white"/>
      </svg>
    `;

  const truckCriticalSVG = `
      <svg width="74" height="74" viewBox="0 0 37 37" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g filter="url(#filter0_d_17001_25041)">
      <rect y="5" width="32" height="32" rx="16" fill="${color}" stroke="white" stroke-width="1.5"/>
      <path d="M19.333 18.333H21.333L23.333 21V24.333H21.7119L21.7031 24.5049C21.6171 25.3532 20.9003 26.0156 20.0293 26.0156C19.1584 26.0154 18.4424 25.3531 18.3564 24.5049L18.3477 24.333H13.6816L13.6729 24.5049C13.5868 25.3531 12.871 26.0156 12 26.0156C11.129 26.0156 10.4132 25.3531 10.3271 24.5049L10.3184 24.333H8.66699V17C8.66699 16.6333 8.79748 16.3197 9.05859 16.0586C9.3197 15.7975 9.63333 15.667 10 15.667H19.333V18.333ZM19.333 19.667V21.667H22.167L20.667 19.667H19.333Z" fill="white"/>
      <rect x="21" width="16" height="16" rx="8" fill="white"/>
      <path d="M29.0002 1.33337C25.3202 1.33337 22.3335 4.32004 22.3335 8.00004C22.3335 11.68 25.3202 14.6667 29.0002 14.6667C32.6802 14.6667 35.6668 11.68 35.6668 8.00004C35.6668 4.32004 32.6802 1.33337 29.0002 1.33337ZM29.6668 11.3334H28.3335V10H29.6668V11.3334ZM29.6668 8.66671H28.3335V4.66671H29.6668V8.66671Z" fill="#DE1135"/>
      </g>
      <defs>
      <filter id="filter0_d_17001_25041" x="-16" y="-12" width="69" height="69" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
      <feFlood flood-opacity="0" result="BackgroundImageFix"/>
      <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
      <feOffset dy="4"/>
      <feGaussianBlur stdDeviation="8"/>
      <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0"/>
      <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_17001_25041"/>
      <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_17001_25041" result="shape"/>
      </filter>
      </defs>
      </svg>
    `;

  const truckRiskSVG = `
    <svg width="76" height="76" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g filter="url(#filter0_d_17007_25152)">
    <rect y="6" width="32" height="32" rx="16" fill="${color}" stroke="white" stroke-width="1.5"/>
    <path d="M19.333 19.333H21.333L23.333 22V25.333H21.7119L21.7031 25.5049C21.6171 26.3532 20.9003 27.0156 20.0293 27.0156C19.1584 27.0154 18.4424 26.3531 18.3564 25.5049L18.3477 25.333H13.6816L13.6729 25.5049C13.5868 26.3531 12.871 27.0156 12 27.0156C11.129 27.0156 10.4132 26.3531 10.3271 25.5049L10.3184 25.333H8.66699V18C8.66699 17.6333 8.79748 17.3197 9.05859 17.0586C9.3197 16.7975 9.63333 16.667 10 16.667H19.333V19.333ZM19.333 20.667V22.667H22.167L20.667 20.667H19.333Z" fill="white"/>
    <path d="M29.5405 2.35315L36.8745 15.0201L37.4175 15.9586H20.5825L21.1255 15.0201L28.4585 2.35315L28.9995 1.41956L29.5405 2.35315ZM28.9585 12.7086H29.0415V12.6246H28.9585V12.7086ZM28.9585 10.0416H29.0415V8.62463H28.9585V10.0416Z" fill="#E6A000" stroke="white" stroke-width="1.25"/>
    </g>
    <defs>
    <filter id="filter0_d_17007_25152" x="-16" y="-11.8282" width="70.5015" height="69.8282" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
    <feFlood flood-opacity="0" result="BackgroundImageFix"/>
    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
    <feOffset dy="4"/>
    <feGaussianBlur stdDeviation="8"/>
    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0"/>
    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_17007_25152"/>
    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_17007_25152" result="shape"/>
    </filter>
    </defs>
    </svg>
  `;

  // const svg = type === "air" ? hasAlert ? isCritical ? airCriticalSVG : airRiskSVG : airSVG : hasAlert ? isCritical ? truckCriticalSVG  : truckRiskSVG :  truckSVG;
  let svg: string;

  if (type === 'air') {
    if (hasAlert) {
      svg = isCritical ? airCriticalSVG : airRiskSVG;
    } else {
      svg = airSVG;
    }
  } else if (type === 'air-delay') {
    svg = createAirportPinSVGAdjusted(delayPercentage, strokeSize);
  } else {
    if (hasAlert) {
      svg = isCritical ? truckCriticalSVG : truckRiskSVG;
    } else {
      svg = truckSVG;
    }
  }

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

/**
 * Compute great-circle distance between two lat/lng points using the
 * Haversine formula. Returns kilometers.
 *
 * Clamps the intermediate value to [0,1] to guard against tiny
 * floating‑point overshoots that can otherwise produce NaN from Math.asin.
 */
export function haversineKm(
  aLat: number,
  aLng: number,
  bLat: number,
  bLng: number
): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const R = 6371; // Earth radius in km
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const s1 = Math.sin(dLat / 2);
  const s2 = Math.sin(dLng / 2);
  const aa = s1 * s1 + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * s2 * s2;
  return 2 * R * Math.asin(Math.sqrt(Math.min(1, aa)));
}

/**
 * Mix two hex colors by a factor t in [0,1].
 * Returns a hex string. Clamps t to the valid range.
 */
export function mixHexColors(c1: string, c2: string, t: number): string {
  const clamp = (x: number, lo = 0, hi = 1) => Math.max(lo, Math.min(hi, x));
  const h = (s: string) => s.replace('#', '');
  const tClamped = clamp(t);
  const a = parseInt(h(c1), 16);
  const b = parseInt(h(c2), 16);
  const r1 = (a >> 16) & 0xff,
    g1 = (a >> 8) & 0xff,
    bl1 = a & 0xff;
  const r2 = (b >> 16) & 0xff,
    g2 = (b >> 8) & 0xff,
    bl2 = b & 0xff;
  const r = Math.round(r1 + (r2 - r1) * tClamped);
  const g = Math.round(g1 + (g2 - g1) * tClamped);
  const bl = Math.round(bl1 + (bl2 - bl1) * tClamped);
  const toHex = (x: number) => x.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(bl)}`;
}

type NetworkInformationLike = {
  effectiveType?: string;
  downlink?: number;
  saveData?: boolean;
};

/**
 * Starting batch size hint for the shipment location stream. The backend
 * (`ShipmentLocations.stream_locations`) clamps it to 1-100 and keeps tuning
 * from there toward ~300ms between batches: fast links get large batches so
 * 10k+ shipments land quickly, constrained links get small ones so the map
 * fills in progressively instead of stalling on huge SSE frames.
 */
export const getStreamBatchSize = (
  connection: NetworkInformationLike | undefined = (
    navigator as Navigator & { connection?: NetworkInformationLike }
  ).connection
): number => {
  if (!connection) return 50;
  const downlink =
    typeof connection.downlink === 'number' &&
    Number.isFinite(connection.downlink)
      ? connection.downlink
      : null;
  if (
    connection.saveData ||
    ['slow-2g', '2g', '3g'].includes(connection.effectiveType ?? '') ||
    (downlink !== null && downlink < 2)
  ) {
    return 20;
  }
  if (downlink !== null && downlink >= 10) return 100;
  return 50;
};

/** RTG codes may contain commas — send one repeated query param per value. */
const STREAM_REPEAT_QUERY_KEYS = new Set(['shipper_rtg', 'consignee_rtg']);

/**
 * Serialize Home map stream filters for EventSource query strings.
 * Non-RTG dims stay comma-joined; RTG dims use repeated keys so codes like
 * `A,B` survive URL decode without being split server-side.
 */
export const buildStreamFilterQueryParams = (
  paramsObj: Record<string, unknown> = {}
): string => {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(paramsObj)) {
    if (!Array.isArray(value) || value.length === 0) continue;
    if (STREAM_REPEAT_QUERY_KEYS.has(key)) {
      for (const item of value) {
        parts.push(
          `${encodeURIComponent(key)}=${encodeURIComponent(String(item))}`
        );
      }
      continue;
    }
    parts.push(
      `${encodeURIComponent(key)}=${value
        .map((item) => encodeURIComponent(String(item)))
        .join(',')}`
    );
  }
  return parts.join('&');
};
